import React, { useContext, useEffect, useState } from 'react';
import { voidFn } from './type';
import { CarouselContext, CarouselContextType } from './CarouselContext';
import { getTargetScreen } from '.';

export type HandleEventProps = {
  elementSelected?: number;
  setElementSelected: React.Dispatch<React.SetStateAction<number>>;
  nbElement: number;
  isLoop?: boolean;
  onClickPrev?: voidFn;
  onClickNext?: voidFn;
  onClickDot?: voidFn;
};

export function handleClickPrev({
  setElementSelected,
  nbElement,
  isLoop,
  onClickPrev,
}: HandleEventProps): void {
  setElementSelected((_count) => {
    if (_count === undefined) {
      _count = 0;
    }
    if (_count !== 0) {
      return _count - 1;
    }
    return isLoop ? nbElement - 1 : _count;
  });
  if (onClickPrev) {
    onClickPrev();
  }
}

export function handleClickNext({
  setElementSelected,
  nbElement,
  isLoop,
  onClickNext,
}: HandleEventProps): void {
  setElementSelected((_count) => {
    if (_count === undefined) {
      _count = 0;
    }
    if (_count < nbElement - 1) {
      return _count + 1;
    }
    return isLoop ? 0 : _count;
  });
  if (onClickNext) {
    onClickNext();
  }
}

export function handleClickDot({
  setElementSelected,
  elementSelected,
  onClickDot,
}: Omit<HandleEventProps, 'nbElement' | 'isLoop'>): void {
  setElementSelected(elementSelected || 0);
  if (onClickDot) {
    onClickDot();
  }
}

export const useScroll = ({
  carouselContainerRef,
  carouselRef,
  elementRefs,
  translateX,
  isItemsCentered,
}) => {
  const { onClickNext, onClickPrev, setElementSelected, elementSelected } =
    useContext<CarouselContextType>(CarouselContext);

  const [isDragging, setIsDragging] = useState(false);
  const [overideTranslate, setOverideTranslate] = useState<number | null>(null);
  const [isMove, setIsMove] = useState(false);
  const [startX, setStartX] = useState(0);
  const [moveX, setMoveX] = useState(0);
  const [forceRerender, setForceRerender] = useState(Date.now());
  const _forceRerender = () => setForceRerender(Date.now());

  const handleMouseDown = (e: any) => {
    setStartX(getEventPosition(e));
    setIsDragging(true);
  };

  const getEventPosition = (event: any): number => {
    return ['onMouseDown', 'onMouseMove'].includes(event?._reactName)
      ? event.pageX
      : event.touches?.[0].pageX; // Return the pageX property based on event type
  };

  const handleMouseMove = (e: any) => {
    if (!isDragging) return;
    setIsMove(true);
    setMoveX(getEventPosition(e));
    const currentPosition = getEventPosition(e); // Get the current position
    const translationAmount = translateX + currentPosition - startX; // Calculate translation amount

    if (carouselRef.current) {
      setOverideTranslate(translationAmount); // Apply translation
    }
  };

  const _setElementSelected = (index) => {
    setElementSelected((prev) => {
      let newIndex = index < 0 ? 0 : index;
      newIndex =
        index >= elementRefs.current.length
          ? elementRefs.current.length - 1
          : newIndex;
      if (newIndex > prev && onClickNext) onClickNext();
      if (newIndex < prev && onClickPrev) onClickPrev();
      return newIndex;
    });
  };

  const getItemTargetPosition = (item: HTMLElement): number => {
    return isItemsCentered
      ? item.getBoundingClientRect().left + item.offsetWidth / 2 // Center position
      : item.getBoundingClientRect().left; // Left position
  };

  const getClosestIndex = (): number => {
    const targetScreen = getTargetScreen(carouselContainerRef, isItemsCentered); // Get target screen
    let closestIndex = 0; // Initialize closest index
    let minDistance = Infinity; // Initialize minimum distance

    // Loop through all items to find the closest one
    elementRefs?.current.forEach((item, index) => {
      const itemTargetPosition = getItemTargetPosition(item); // Get item's target position
      const distanceToTarget = Math.abs(itemTargetPosition - targetScreen); // Calculate distance

      // Update closest index if a closer item is found
      if (distanceToTarget < minDistance) {
        minDistance = distanceToTarget;
        closestIndex = index;
      }
    });

    return closestIndex; // Return the closest index
  };

  const handleMouseUp = (e: any) => {
    if (Math.abs(Math.abs(moveX) - Math.abs(startX)) > 20) {
      const closestIndex = getClosestIndex();
      if (elementSelected !== closestIndex) _setElementSelected(closestIndex);
      else if (startX < moveX) _setElementSelected(elementSelected - 1);
      else if (startX > moveX) _setElementSelected(elementSelected + 1);
    }
    setMoveX(0);
    setOverideTranslate(null);
    setIsDragging(false);
    setIsMove(false);
    _forceRerender();
  };

  return {
    forceRerender,
    handleMouseUp,
    handleMouseMove,
    handleMouseDown,
    isDragging,
    isMove,
    overideTranslate,
  };
};

export const useAutoScroll = (elementRefs) => {
  const { isAutoplay, setElementSelected } =
    useContext<CarouselContextType>(CarouselContext);

  useEffect(() => {
    if (isAutoplay) {
      const intervalId = setInterval(
        () => {
          handleClickNext({
            setElementSelected,
            nbElement: elementRefs.current.length,
            isLoop: true,
          });
        },
        typeof isAutoplay === 'number' ? isAutoplay : 5000,
      );

      return () => clearInterval(intervalId);
    }
  }, [isAutoplay, elementRefs, setElementSelected]);
};

export const preventDefaultAction = (event) => {
  event.preventDefault();
};

export const useDisableChildrenLink = (refs, isMove, isDragging) => {
  useEffect(() => {
    const carroussel = refs.current;
    const links = carroussel.querySelectorAll('a');

    if (isMove) {
      links?.forEach((link) => {
        link.addEventListener('click', preventDefaultAction);
      });
    }

    return () => {
      if (!isMove)
        links?.forEach((link) => {
          link.removeEventListener('click', preventDefaultAction);
        });
    };
  }, [refs, isDragging, isMove]);
};
